import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import type { View } from '../types';
import { VisuallyHidden } from './ui/visually-hidden';
import { NotesPopup } from './NotesPopup';
import { 
  Share2, Navigation, Ruler, X, MapPin, ChevronLeft, ChevronRight, 
  ZoomIn, ZoomOut, Move, Maximize, Download,
  Info, Eye, EyeOff, ChevronUp, ChevronDown, Image,
  StickyNote, Check, Play, Pause, Clock, Fullscreen
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getImageUrl, getDownloadUrl } from '../lib/imageUtils';

// Fix NodeJS namespace for Timeout types
type TimeoutId = ReturnType<typeof setTimeout>;

// Add resource_3 and resource_4 to the interface if not already in types.ts
interface ViewWithCredit extends View {
  resource_3?: string; // Credit Link
  resource_4?: string; // Credit Info
  notes?: string; // Custom user notes for this view
}

interface ViewModalProps {
  view: ViewWithCredit | null;
  open: boolean;
  onClose: () => void;
  onShare: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onUpdateNotes?: (id: string, notes: string) => void; // New callback to save notes
}

export function ViewModal({ 
  view, 
  open, 
  onClose, 
  onShare, 
  onNext, 
  onPrevious,
  onUpdateNotes
}: ViewModalProps) {
  if (!view) return null;

  // Debug mode for deployment troubleshooting
  const isDebugMode = process.env.NODE_ENV === 'development' || 
    (typeof window !== 'undefined' && window.location.search.includes('debug=true'));

  // Debug logging helper
  const debugLog = useCallback((message: string, data?: any) => {
    if (isDebugMode) {
      // Debug logging disabled
    }
  }, [isDebugMode]);

  // Debug: Log the view object to see what's actually in it
  if (isDebugMode) {
      // ViewModal debug information
  }

  // State declarations
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [notesValue, setNotesValue] = useState("");
  const [imageContainerDimensions, setImageContainerDimensions] = useState<{ width: number; height: number } | null>(null);
  const [isNotesVisible, setIsNotesVisible] = useState(true);
  const [isRegularPanelVisible, setIsRegularPanelVisible] = useState(true);
  const [isMinimal, setIsMinimal] = useState(false);

  // Auto-cycle state for fullscreen mode
  const [isAutoCycling, setIsAutoCycling] = useState(false);
  const [autoCycleInterval, setAutoCycleInterval] = useState(30); // seconds
  const [showIntervalSelector, setShowIntervalSelector] = useState(false);

  // Log fullscreen state changes for debugging
  useEffect(() => {
    debugLog('Fullscreen state changed', { isFullscreen, open });
  }, [isFullscreen, open, debugLog]);

  // Refs
  const dialogRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  // Outer wrapper - the element that goes fullscreen. Does NOT frame the image.
  const imageContainerRef = useRef<HTMLDivElement>(null);
  // The box the image is actually laid out and centred in. All zoom/pan maths
  // must measure this one: the outer wrapper is `flex-grow` and can be taller
  // than the image frame, which pushes the zoom anchor off centre.
  const imageFrameRef = useRef<HTMLDivElement>(null);
  const notesButtonRef = useRef<HTMLButtonElement>(null);
  const startMousePos = useRef({ x: 0, y: 0 });
  const startPanelWidth = useRef(350);
  const startDragPos = useRef({ x: 0, y: 0 });
  const zoomTweenRef = useRef<number | null>(null);
  const autoCycleIntervalRef = useRef<TimeoutId | null>(null);
  const onNextRef = useRef(onNext);
  const maxZoom = 5;
  const zoomStep = 1.5; // multiplicative, so a press feels the same at 1x and at 4x

  // Live mirrors of zoom/position. Several wheel events can arrive inside one
  // React batch; reading state directly there would use a stale value and make
  // the anchor drift. These are written synchronously by zoomAt/pan.
  const zoomRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });

  // State for zoom and pan functionality
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0, ratio: 0 });
  const [isWideImage, setIsWideImage] = useState(false);
  const [showWidePanoramicView, setShowWidePanoramicView] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showDetailsInFullscreen, setShowDetailsInFullscreen] = useState(true);
  const [detailsPanelState, setDetailsPanelState] = useState<'expanded' | 'collapsed' | 'hidden'>('expanded');
  const [isSmallImage, setIsSmallImage] = useState(false);
  const [isActualSize, setIsActualSize] = useState(false);
  
  // Auto-cycle interval options
  const INTERVAL_OPTIONS = [
    { value: 5, label: '5s' },
    { value: 15, label: '15s' },
    { value: 30, label: '30s' },
    { value: 60, label: '1m' },
    { value: 120, label: '2m' },
    { value: 300, label: '5m' }
  ];
  
  // Constants for panel heights - using viewport-relative values
  const getResponsivePanelHeight = (state: 'expanded' | 'collapsed' | 'hidden'): number => {
    if (state === 'hidden') return 0;
    if (typeof window === 'undefined') {
      return state === 'expanded' ? 675 : 387;
    }
    const vh = window.innerHeight;
    if (state === 'expanded') {
      return Math.min(675, vh * 0.6); // Max 60% of viewport
    }
    return Math.min(387, vh * 0.35); // Max 35% of viewport
  };

  const PANEL_HEIGHTS = {
    expanded: getResponsivePanelHeight('expanded'),
    collapsed: getResponsivePanelHeight('collapsed'),
    hidden: 0
  };
  
  // Notes state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(view.notes || '');
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Update notes draft when view changes
  useEffect(() => {
    if (view) {
      setNotesDraft(view.notes || '');
      setIsEditingNotes(false);
    }
  }, [view]);
  
  // Save notes handler
  const handleSaveNotes = useCallback(() => {
    if (onUpdateNotes && view) {
      onUpdateNotes(view.id, notesDraft);
    }
    setIsEditingNotes(false);
  }, [notesDraft, onUpdateNotes, view]);
  
  // Handle notes editor focus when editing starts
  useEffect(() => {
    if (isEditingNotes && notesTextareaRef.current) {
      notesTextareaRef.current.focus();
    }
  }, [isEditingNotes]);
  
  // Keep onNext ref updated
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);
  
  // Auto-cycle functionality - using setInterval for more stability
  const startAutoCycle = useCallback(() => {
    if (!onNextRef.current) return;
    
    // Clear any existing interval
    if (autoCycleIntervalRef.current) {
      clearInterval(autoCycleIntervalRef.current);
    }
    
    setIsAutoCycling(true);
    setShowIntervalSelector(false);
    
    // Use setInterval instead of recursive setTimeout
    autoCycleIntervalRef.current = setInterval(() => {
      if (onNextRef.current) {
        onNextRef.current();
        // Reset zoom/pan when auto-cycling to next view
        setTimeout(() => {
          resetView();
        }, 100); // Small delay to ensure view has changed
      }
    }, autoCycleInterval * 1000);
  }, [autoCycleInterval]);
  
  const stopAutoCycle = useCallback(() => {
    setIsAutoCycling(false);
    
    if (autoCycleIntervalRef.current) {
      clearInterval(autoCycleIntervalRef.current);
      autoCycleIntervalRef.current = null;
    }
  }, []);
  
  // Clean up auto-cycle interval on unmount
  useEffect(() => {
    return () => {
      if (autoCycleIntervalRef.current) {
        clearInterval(autoCycleIntervalRef.current);
        autoCycleIntervalRef.current = null;
      }
    };
  }, []);
  
  // Stop auto-cycle when modal closes or exits fullscreen
  useEffect(() => {
    if (!open || !isFullscreen) {
      stopAutoCycle();
    }
  }, [open, isFullscreen]);
  
  // Panoramic carousel effect
  const panoramaX = useMotionValue(0);
  const panoramaOpacity = useTransform(
    panoramaX,
    [-100, 0, 100], 
    [0.3, 1, 0.3]
  );

  const [isPanoramic, setIsPanoramic] = useState(false);
  const [scale, setScale] = useState(1);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  
  // Reset zoom and pan
  const resetView = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    panoramaX.set(0);
    setShowWidePanoramicView(false);
  }, [panoramaX]);

  // Toggle panoramic view for wide images
  const togglePanoramicView = useCallback(() => {
    setShowWidePanoramicView(!showWidePanoramicView);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    panoramaX.set(0);
  }, [showWidePanoramicView, panoramaX]);

  // Toggle UI overlay
  const toggleOverlay = useCallback(() => setShowOverlay(!showOverlay), [showOverlay]);

  // Add function to download image
  const downloadImage = useCallback(() => {
    const a = document.createElement('a');
    a.href = getDownloadUrl(view);
    a.download = `view_${view.id}_${view.source}_to_${view.destination}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [view]);

  // Enhanced container dimensions update with debouncing for performance
  useEffect(() => {
    let timeoutId: TimeoutId | null = null;
    
    const updateImageContainerDimensions = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      timeoutId = setTimeout(() => {
        if (imageContainerRef.current) {
          try {
            const rect = imageContainerRef.current.getBoundingClientRect();
            setImageContainerDimensions({
              width: rect.width,
              height: rect.height
            });
          } catch (error) {
            console.warn('Failed to get container dimensions:', error);
          }
        }
      }, 16); // ~60fps debouncing
    };

    // Initial update
    updateImageContainerDimensions();
    
    // Add event listener for window resize
    window.addEventListener('resize', updateImageContainerDimensions);
    
    // Also listen for orientation changes on mobile devices
    window.addEventListener('orientationchange', updateImageContainerDimensions);
    
    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', updateImageContainerDimensions);
      window.removeEventListener('orientationchange', updateImageContainerDimensions);
    };
  }, []);

  // Enhanced fullscreen implementation with better browser compatibility
  const handleFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement) {
        // Try multiple exit methods for better compatibility
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(err => {
            console.error(`Error exiting fullscreen: ${err.message}`);
          });
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
      } else if (imageContainerRef.current) {
        const element = imageContainerRef.current;
        
        // Try multiple fullscreen methods for better compatibility
        if (element.requestFullscreen) {
          element.requestFullscreen().catch(err => {
            console.error(`Error entering fullscreen: ${err.message}`);
          });
        } else if ((element as any).webkitRequestFullscreen) {
          (element as any).webkitRequestFullscreen();
        } else if ((element as any).mozRequestFullScreen) {
          (element as any).mozRequestFullScreen();
        } else if ((element as any).msRequestFullscreen) {
          (element as any).msRequestFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen operation failed:', error);
    }
  }, []);

  // Enhanced fullscreen change detection with multiple event types
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Check multiple fullscreen properties for better compatibility
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      
      setIsFullscreen(isCurrentlyFullscreen);
      
      // When entering fullscreen, collapse the panel
      if (isCurrentlyFullscreen) {
        setDetailsPanelState('collapsed');
        // Reset zoom state when entering fullscreen
        setZoom(1);
        setPosition({ x: 0, y: 0 });
      } else {
        // Reset panel state when exiting fullscreen
        resetView();
      }
    };

    // Add multiple event listeners for better browser compatibility
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];
    
    events.forEach(eventName => {
      document.addEventListener(eventName, handleFullscreenChange);
    });
    
    return () => {
      events.forEach(eventName => {
        document.removeEventListener(eventName, handleFullscreenChange);
      });
    };
  }, [resetView]);

  // Check image dimensions when loaded
  const handleImageLoad = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      const ratio = naturalWidth / naturalHeight;
      
      setOriginalImageDimensions({
        width: naturalWidth,
        height: naturalHeight,
        ratio
      });
      
      // Consider an image "wide" if its ratio is greater than 2.5:1
      setIsWideImage(ratio > 2.5);
      
      // Check if image is at "actual size" when first loaded (no scaling needed)
      const frame = imageFrameRef.current || imageContainerRef.current;
      if (frame) {
        const containerRect = frame.getBoundingClientRect();
        const smallImage = naturalWidth <= containerRect.width && naturalHeight <= containerRect.height;
        
        setIsSmallImage(smallImage);
        setIsActualSize(smallImage);
        
        // If small image, set zoom to 1 to display at actual size
        if (smallImage) {
          setZoom(1);
        }
      }
    }
  };

  // Update actual size status when fullscreen changes or on resize
  useEffect(() => {
    const frame = imageFrameRef.current || imageContainerRef.current;
    if (imageRef.current && frame) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      const containerRect = frame.getBoundingClientRect();

      // In fullscreen, check against window dimensions
      const containerWidth = isFullscreen ? window.innerWidth : containerRect.width;
      const containerHeight = isFullscreen ? window.innerHeight : containerRect.height;
      
      const smallImage = naturalWidth <= containerWidth && naturalHeight <= containerHeight;
      setIsSmallImage(smallImage);
      
      // Only update isActualSize if zoom is 1
      if (zoom === 1) {
        setIsActualSize(smallImage);
      } else {
        setIsActualSize(false);
      }
    }
  }, [isFullscreen, zoom]);

  // Toggle details visibility in fullscreen
  const toggleDetailsInFullscreen = () => {
    if (isFullscreen) {
      setShowDetailsInFullscreen(!showDetailsInFullscreen);
    }
  };

  // Fix the togglePanelState function to properly cycle through states
  const togglePanelState = () => {
    if (detailsPanelState === 'expanded') {
      setDetailsPanelState('collapsed');
    } else if (detailsPanelState === 'collapsed') {
      setDetailsPanelState('hidden');
    } else {
      // Coming from hidden state, go back to collapsed (not expanded)
      setDetailsPanelState('collapsed');
    }
  };

  // When entering fullscreen, ensure panel is in collapsed state
  useEffect(() => {
    if (isFullscreen) {
      setDetailsPanelState('collapsed');
    }
  }, [isFullscreen]);

  // Both figures come from the API (lib/api.ts), so the secondary unit is the
  // recorded mileage rather than a conversion of the kilometre value.
  const distance = { value: view.distanceKm, unit: 'km' };
  const otherUnit = 'mi';
  const otherValue = view.distanceMi.toFixed(2);

  // Status badge color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed': return 'bg-green-500';
      case 'likely': return 'bg-blue-500';
      case 'ifclear': return 'bg-cyan-500';
      case 'plausible': return 'bg-yellow-500';
      case 'dubious': return 'bg-orange-500';
      case 'unlikely': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Keep the live mirrors in step with state for changes that bypass zoomAt
  // (reset, fullscreen transitions, view switches).
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { positionRef.current = position; }, [position]);

  // Bound the pan so an image edge can reach the frame edge but never cross it.
  // Measured against the image's laid-out box, not the frame, because
  // object-contain letterboxes it - offsetWidth/Height are unaffected by the
  // CSS transform, so they give the untransformed size.
  const clampPan = useCallback((p: { x: number; y: number }, z: number) => {
    if (z <= 1.001) return { x: 0, y: 0 };

    const frame = imageFrameRef.current;
    const image = imageRef.current;
    if (!frame || !image) return p;

    let frameRect;
    try {
      frameRect = frame.getBoundingClientRect();
    } catch (error) {
      return p;
    }

    const imageWidth = image.offsetWidth || frameRect.width;
    const imageHeight = image.offsetHeight || frameRect.height;

    // Symmetric difference, not max(0, ...). When the scaled image is larger
    // than the frame this stops an edge coming inside it. When it is smaller -
    // the usual case here, since a 16:9 image is letterboxed in this frame -
    // it still allows travel across the letterbox margin, so the zoom anchor
    // can be honoured instead of being locked to centre. Below 1x it recentres.
    const maxX = Math.abs(imageWidth * z - frameRect.width) / 2;
    const maxY = Math.abs(imageHeight * z - frameRect.height) / 2;

    return {
      x: Math.min(maxX, Math.max(-maxX, p.x)),
      y: Math.min(maxY, Math.max(-maxY, p.y))
    };
  }, []);

  // The whole fix. Change the scale while holding one point still: solve for
  // the offset that keeps (clientX, clientY) over the same pixel it was over
  // before. Every path that changes zoom goes through here - the only thing
  // that differs between them is which point is passed as the anchor.
  const zoomAt = useCallback((clientX: number, clientY: number, nextZoom: number) => {
    const frame = imageFrameRef.current;
    if (!frame) return;

    let frameRect;
    try {
      frameRect = frame.getBoundingClientRect();
    } catch (error) {
      console.warn('getBoundingClientRect failed, skipping zoom');
      return;
    }

    const prevZoom = zoomRef.current;
    const prevPosition = positionRef.current;
    const z = Math.max(1, Math.min(maxZoom, nextZoom));
    if (Math.abs(z - prevZoom) < 0.0001) return;

    const factor = z / prevZoom;

    // Vector from the image centre as currently drawn to the anchor point.
    // Clamped to the frame so a pointer over the letterbox margin anchors to
    // the nearest edge rather than to empty space.
    const anchorX = Math.min(frameRect.width, Math.max(0, clientX - frameRect.left));
    const anchorY = Math.min(frameRect.height, Math.max(0, clientY - frameRect.top));
    const distX = anchorX - frameRect.width / 2 - prevPosition.x;
    const distY = anchorY - frameRect.height / 2 - prevPosition.y;

    const nextPosition = clampPan({
      x: prevPosition.x + distX - distX * factor,
      y: prevPosition.y + distY - distY * factor
    }, z);

    zoomRef.current = z;
    positionRef.current = nextPosition;
    setZoom(z);
    setPosition(nextPosition);
  }, [clampPan, maxZoom]);

  // A button or key press carries no pointer position, so the anchor becomes
  // the centre of the frame - the thing the viewer is already looking at.
  const zoomAtFrameCentre = useCallback((nextZoom: number) => {
    const frame = imageFrameRef.current;
    if (!frame) {
      setZoom(Math.max(1, Math.min(maxZoom, nextZoom)));
      return;
    }

    try {
      const rect = frame.getBoundingClientRect();
      zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, nextZoom);
    } catch (error) {
      console.warn('Frame-centre zoom failed:', error);
    }
  }, [zoomAt, maxZoom]);

  // Double-click steps through fixed stops, holding the clicked point still for
  // the whole tween rather than only at its endpoints.
  const doubleClickStops = [2, 4];

  const zoomToPoint = useCallback((clientX: number, clientY: number, target: number) => {
    if (zoomTweenRef.current !== null) {
      cancelAnimationFrame(zoomTweenRef.current);
      zoomTweenRef.current = null;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      zoomAt(clientX, clientY, target);
      return;
    }

    const from = zoomRef.current;
    const start = performance.now();
    const duration = 220;

    const step = (now: number) => {
      const k = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      zoomAt(clientX, clientY, from + (target - from) * eased);

      if (k < 1) {
        zoomTweenRef.current = requestAnimationFrame(step);
      } else {
        zoomTweenRef.current = null;
      }
    };

    zoomTweenRef.current = requestAnimationFrame(step);
  }, [zoomAt]);

  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    // Next stop above the current scale, wrapping back to fit past the last one
    const next = doubleClickStops.find(stop => stop > zoomRef.current + 0.01);

    if (next === undefined) {
      resetView();
      return;
    }

    zoomToPoint(e.clientX, e.clientY, next);
  }, [zoomToPoint, resetView]);

  // The scrubber is mapped logarithmically, otherwise the useful low end of the
  // range is squashed into the first few percent of the track.
  const zoomToSlider = useCallback((z: number) => {
    return (Math.log(z) / Math.log(maxZoom)) * 100;
  }, [maxZoom]);

  const sliderToZoom = useCallback((value: number) => {
    return Math.exp((value / 100) * Math.log(maxZoom));
  }, [maxZoom]);

  // Pan bounds are a function of the frame size, so re-apply them whenever it
  // changes - otherwise a resize can leave the image parked outside the frame.
  useEffect(() => {
    const reclamp = () => {
      if (zoomRef.current <= 1.001) return;
      const next = clampPan(positionRef.current, zoomRef.current);
      if (next.x !== positionRef.current.x || next.y !== positionRef.current.y) {
        positionRef.current = next;
        setPosition(next);
      }
    };

    window.addEventListener('resize', reclamp);
    window.addEventListener('orientationchange', reclamp);
    return () => {
      window.removeEventListener('resize', reclamp);
      window.removeEventListener('orientationchange', reclamp);
    };
  }, [clampPan]);

  // Handle mouse wheel zoom with improved deployment compatibility
  const handleWheel = useCallback((e: WheelEvent) => {
    // Allow zooming in both fullscreen and regular view modes
    try {
      e.preventDefault();
    } catch (error) {
      // Fallback for passive event listeners in production
      console.warn('preventDefault failed on wheel event - passive listener detected');
    }
    
    if (!imageFrameRef.current || !imageRef.current) return;

    // Multiplicative step, damped at high zoom so the top of the range stays
    // controllable. Normalising by deltaMode keeps line-scrolling mice and
    // pixel-precise trackpads roughly in step.
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
    const delta = e.deltaY * unit;
    const sensitivity = isFullscreen ? 0.0022 : 0.0018;
    const damping = zoomRef.current > 3 ? 0.55 : 1;
    const step = Math.exp(-delta * sensitivity * damping);

    // The pointer is the anchor: whatever is under the cursor stays put.
    zoomAt(e.clientX, e.clientY, zoomRef.current * step);
  }, [isFullscreen, zoomAt]);

  // Wheel listener, bound to the frame the image is laid out in. Non-passive so
  // the page behind the modal does not scroll; the React onWheel on the same
  // subtree covers the case where that registration is refused.
  useEffect(() => {
    const frame = imageFrameRef.current;
    if (!frame) return;

    try {
      frame.addEventListener('wheel', handleWheel, { passive: false, capture: false });
    } catch (error) {
      console.warn('Failed to add wheel listener, falling back to React onWheel:', error);
      return;
    }

    return () => {
      frame.removeEventListener('wheel', handleWheel);
    };
    // showWidePanoramicView remounts the frame, so the listener must re-bind
  }, [handleWheel, showWidePanoramicView]);

  // Enhanced mouse down handler with better error handling
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    
    try {
      // Record starting position
      startDragPos.current = { x: position.x, y: position.y };
      startMousePos.current = { x: e.clientX, y: e.clientY };
      setIsPanning(true);
      
      // Prevent image drag behavior
      e.preventDefault();
    } catch (error) {
      console.warn('Mouse down handler failed:', error);
    }
  };
  
  // Enhanced mouse move handler with better error handling
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPanning || zoom <= 1) return;
    
    try {
      // Calculate the distance moved
      const deltaX = e.clientX - startMousePos.current.x;
      const deltaY = e.clientY - startMousePos.current.y;

      // Update position, bounded so the image cannot be dragged out of frame
      const nextPosition = clampPan({
        x: startDragPos.current.x + deltaX,
        y: startDragPos.current.y + deltaY
      }, zoom);

      positionRef.current = nextPosition;
      setPosition(nextPosition);

      // Prevent text selection during drag
      e.preventDefault();
    } catch (error) {
      console.warn('Mouse move handler failed:', error);
    }
  };
  
  // Enhanced mouse up handler with better error handling
  const handleMouseUp = () => {
    try {
      setIsPanning(false);
    } catch (error) {
      console.warn('Mouse up handler failed:', error);
    }
  };

  /**
   * Keep the newest handler in a ref so the listener below can stay registered
   * on `open` alone without capturing stale state.
   *
   * The previous version registered the handler with `[open]` deps and read
   * `isFullscreen` / `isAutoCycling` / `isWideImage` straight from the closure,
   * so those were frozen at the value they had when the modal opened — pressing
   * `A` after going fullscreen saw `isFullscreen === false` and did nothing.
   * Only zoom escaped it, via `zoomRef`.
   */
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});

  useEffect(() => {
    keyHandlerRef.current = (e: KeyboardEvent) => {
      // Never hijack keys while someone is typing.
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName ?? '')) {
        return;
      }

      if (e.key === 'ArrowLeft' && onPrevious) {
        e.preventDefault(); // Prevent scrolling
        e.stopPropagation(); // Stop event from bubbling
        onPrevious();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault(); // Prevent scrolling
        e.stopPropagation(); // Stop event from bubbling
        onNext();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        // Same anchor as the toolbar buttons - zoomAt shows the indicator
        zoomAtFrameCentre(zoomRef.current * zoomStep);
      } else if (e.key === '-') {
        e.preventDefault();
        zoomAtFrameCentre(zoomRef.current / zoomStep);
      } else if (e.key === '0') {
        e.preventDefault();
        resetView();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        handleFullscreen();
      } else if (e.key === 'o' || e.key === 'O') {
        e.preventDefault();
        toggleOverlay();
      } else if ((e.key === 'p' || e.key === 'P') && isWideImage) {
        // Parenthesised deliberately: `&&` binds tighter than `||`, so the old
        // `e.key === 'p' || e.key === 'P' && isWideImage` meant lowercase `p`
        // toggled panoramic on every image, wide or not.
        e.preventDefault();
        togglePanoramicView();
      } else if (e.key === 'd' || e.key === 'D') {
        e.preventDefault();
        downloadImage();
      } else if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        document.exitFullscreen().catch(err => {
          console.error(`Error exiting fullscreen: ${err.message}`);
        });
      } else if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        if (isFullscreen && onNext) {
          if (isAutoCycling) {
            stopAutoCycle();
          } else {
            startAutoCycle();
          }
        }
      }
    };
  }); // no dep array: refresh the handler every render so it never goes stale

  /**
   * Register on the document, not on the dialog element.
   *
   * The listener used to be bound to `dialogRef.current`, guarded by
   * `if (dialogElement)`. Radix mounts DialogContent through its Presence
   * component, so at the moment this effect ran the ref was still null, the
   * guard silently skipped registration, and `[open]` deps meant it never
   * retried. Confirmed against production with CDP getEventListeners: the
   * dialog node had zero keydown listeners. Every shortcut except the arrows
   * — which are handled separately by the onKeyDown prop below — was dead.
   *
   * A document listener has no such timing dependency. It is added only while
   * the modal is open, so it cannot leak into the rest of the app.
   */
  useEffect(() => {
    if (!open) return;

    const focusTimer = setTimeout(() => {
      dialogRef.current?.focus();
    }, 100);

    const onKeyDown = (e: KeyboardEvent) => keyHandlerRef.current(e);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Reset view when modal is opened
  useEffect(() => {
    if (open) {
      resetView();
    }
  }, [open, resetView]);

  // Cancel any in-flight zoom tween when the component unmounts
  useEffect(() => {
    return () => {
      if (zoomTweenRef.current !== null) {
        cancelAnimationFrame(zoomTweenRef.current);
        zoomTweenRef.current = null;
      }
    };
  }, []);

  // Add a button to toggle notes panel
  const toggleNotesPanel = () => {
    setShowNotesPanel(prev => !prev);
  };

  // The notes popup is read-only, so with nothing to show the button would only
  // ever open an empty panel. Hide it entirely in that case.
  const hasNotes = Boolean(view.notes && view.notes.trim());

  return (
    <Dialog 
      open={open} 
      onOpenChange={(value) => !value && onClose()}
    >
      <DialogContent
        data-testid="view-modal"
        className="w-full h-full max-w-full max-h-full sm:max-w-[90vw] sm:max-h-[88vh] md:max-w-[92vw] p-0 border-none bg-gray-950 shadow-xl focus:outline-none outline-none rounded-none sm:rounded-lg ring-0 ring-offset-0 border-l-2 border-cyan-500/40 sm:top-[45%] top-0"
        ref={dialogRef}
        tabIndex={0}
        // Arrow keys used to be handled here as well as in the document
        // listener. That duplicate is deliberately gone: this prop was the only
        // reason ←/→ worked at all while every other shortcut was dead, and
        // keeping both would now navigate two records per press.
        data-keyboard-trap="true"
        style={{ outline: 'none' }}
      >
        <VisuallyHidden className={isFullscreen ? 'z-[9999999]' : ''}>
          <DialogTitle>
            View from {view.source} to {view.destination}
          </DialogTitle>
          <DialogDescription>
            Image viewer showing the view from {view.source} to {view.destination}, distance: {view.distance}. Use arrow keys to navigate between views, mouse wheel to zoom, and drag to pan the image.
          </DialogDescription>
        </VisuallyHidden>
        <div className={`flex flex-col ${isFullscreen ? 'h-screen' : 'max-h-[88vh] h-full'}`}>
          <div className={`grid grid-cols-3 items-center p-2 bg-gray-900 ${isFullscreen ? 'relative z-[999999]' : ''}`}>
            <div className="flex items-center space-x-2">
              {/* Left side controls */}
            </div>
            
            {/* Centered caption - using grid for proper centering */}
            <div className="flex justify-center items-center">
              {view && (
                <h2 className="text-white text-lg font-bold whitespace-nowrap truncate max-w-full px-2">
                  {view.destination} viewed from {view.source}
                </h2>
              )}
            </div>
            
            <div className="flex items-center justify-end space-x-2">
              {/* Notes button moved to bottom of screen */}
            </div>
          </div>
          <div 
            ref={imageContainerRef}
            className={`relative ${isFullscreen ? 'w-full h-full' : 'flex-grow'}`}
          >
            {/* The container for the image */}
            <div className={`relative overflow-hidden bg-black ${isFullscreen ? 'w-full h-full' : 'h-[54vh]'}`}>
              {/* Animated tech border line effect */}
              <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500/20 via-cyan-400/60 to-blue-500/20 animate-pulse" />
              
              {/* Image container with advanced controls - Enhanced for deployment */}
              <div 
                className={cn(
                  "w-full h-full relative bg-gray-950 overflow-hidden",
                  // Add proper CSS containment for fullscreen
                  isFullscreen && "contain-layout contain-style",
                  // Ensure proper positioning context
                  "isolate"
                )}
                title={isFullscreen ? "Use mouse wheel to zoom in/out" : ""}
                style={{
                  // Ensure proper rendering in fullscreen
                  position: isFullscreen ? 'relative' : 'relative',
                  // Improve performance
                  willChange: isFullscreen ? 'transform, opacity' : 'auto'
                }}
              >
                {/* Panoramic view for ultra-wide images */}
                {showWidePanoramicView && isWideImage ? (
                  <motion.div 
                    className="w-full h-full flex items-center justify-center overflow-hidden"
                    style={{ 
                      x: panoramaX,
                      opacity: panoramaOpacity
                    }}
                  >
                    <img
                      src={getImageUrl(view, 'full')}
                      alt={`View from ${view.source} to ${view.destination}`}
                      className="h-full object-cover"
                      style={{ 
                        maxWidth: 'none', 
                        width: 'auto',
                        imageRendering: 'auto' 
                      }}
                      loading="eager"
                      onLoad={handleImageLoad}
                      ref={imageRef}
                    />
                  </motion.div>
                ) : (
                  /* Interactive zoom and pan for normal view - Enhanced for deployment compatibility */
                  <div 
                    className={cn(
                      "w-full h-full flex items-center justify-center overflow-hidden",
                      // Add CSS containment for better performance in production
                      "contain-layout contain-style"
                    )}
                    style={{ 
                      cursor: isPanning ? 'grabbing' : (zoom > 1 ? 'grab' : 'default'),
                      // Ensure proper touch-action for mobile devices
                      touchAction: zoom > 1 ? 'none' : 'auto',
                      // Improve hardware acceleration
                      willChange: isPanning ? 'transform' : 'auto'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onDoubleClick={handleDoubleClick}
                    // Add touch event handlers for mobile compatibility
                    onTouchStart={(e) => {
                      if (e.touches.length === 1 && zoom > 1) {
                        const touch = e.touches[0];
                        handleMouseDown({
                          clientX: touch.clientX,
                          clientY: touch.clientY,
                          preventDefault: () => e.preventDefault()
                        } as any);
                      }
                    }}
                    onTouchMove={(e) => {
                      if (e.touches.length === 1 && isPanning) {
                        const touch = e.touches[0];
                        handleMouseMove({
                          clientX: touch.clientX,
                          clientY: touch.clientY,
                          preventDefault: () => e.preventDefault()
                        } as any);
                      }
                    }}
                    onTouchEnd={handleMouseUp}
                    // Fallback wheel handler for when addEventListener fails
                    onWheel={(e) => {
                      // Only use as fallback if main listener isn't working
                      if (!e.defaultPrevented) {
                        try {
                          handleWheel(e.nativeEvent);
                        } catch (error) {
                          console.warn('Fallback wheel handler failed:', error);
                        }
                      }
                    }}
                  >
                    <div
                      className={cn(
                        "w-full h-full flex items-center justify-center",
                        // Additional CSS for deployment stability
                        "relative isolate"
                      )}
                      ref={imageFrameRef}
                      style={{
                        // Ensure proper stacking context
                        zIndex: 1,
                        // Improve rendering performance
                        backfaceVisibility: 'hidden',
                        perspective: 1000
                      }}
                    >
                      <img
                        src={getImageUrl(view, 'full')}
                        alt={`View from ${view.source} to ${view.destination}`}
                        className="w-auto h-auto max-w-full max-h-full object-contain"
                        style={{ 
                          transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                          transformOrigin: 'center',
                          imageRendering: 'auto',
                          transition: isPanning ? 'none' : 'transform 0.05s ease-out',
                          objectFit: isFullscreen && isSmallImage && zoom === 1 ? 'none' : 'contain',
                          // Improve image rendering performance
                          backfaceVisibility: 'hidden',
                          // Ensure proper layer compositing
                          willChange: isPanning || zoom !== 1 ? 'transform' : 'auto'
                        }}
                        loading="eager"
                        onLoad={handleImageLoad}
                        ref={imageRef}
                        draggable={false}
                        // Additional touch handlers for better mobile experience
                        onTouchStart={(e) => e.preventDefault()}
                      />
                      
                      {/* Add indicator badge for actual size display */}
                      {isActualSize && (
                        <div className="absolute bottom-16 left-4 px-3 py-1 bg-green-900/70 backdrop-blur-sm rounded-md text-white border border-green-500/30 text-xs flex items-center gap-1.5 shadow-lg z-10">
                          <Image className="h-3.5 w-3.5 text-green-400" />
                          <span className="text-green-300 font-medium">Natural Size</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {showOverlay && (
                  <>
                    {/* Refined hexagonal grid overlay for sci-fi effect */}
                    <div className="absolute inset-0 pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgMmw3LjI1IDEyLjU2TDM0LjUgMjdIMjBsLTE0LjUtMTIuNDRMMjAgMnoiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLDIwMCwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')] opacity-30" />
                    
                    {/* Glowing frame edge */}
                    <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_30px_rgba(0,180,255,0.1)]" />
                  </>
                )}
                
                {showOverlay && (
                  <>
                    {/* Top right controls - Close and Share buttons */}
                    <div className="absolute top-[6px] right-4 flex items-center gap-3" style={{ zIndex: 99999999 }}>
                      {/* Share button - with hover glow */}
                      <button
                        type="button"
                        onClick={onShare}
                        className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 backdrop-blur-sm rounded-full text-white transition-all duration-300 border border-cyan-400/30 hover:border-cyan-300/50 shadow-none hover:shadow-[0_0_15px_rgba(0,200,255,0.5),0_0_30px_rgba(0,200,255,0.25)]"
                        style={{ zIndex: 99999999 }}
                        aria-label="Share view"
                      >
                        <Share2 className="h-5 w-5" />
                        <span className="font-medium text-sm">Share</span>
                      </button>

                      {/* Close button */}
                      <button
                        type="button"
                        onClick={onClose}
                        className="p-2 bg-gray-900/80 backdrop-blur-sm rounded-full text-white hover:bg-cyan-600/40 transition-colors border border-cyan-500/20 shadow-[0_0_8px_rgba(0,200,255,0.2)]"
                        aria-label="Close dialog"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Fullscreen header overlay - positioned above everything */}
                    {isFullscreen && (
                      <div className="fixed top-0 left-0 right-0 z-[9999999] bg-gray-900/80 backdrop-blur-sm border-b border-cyan-500/20">
                        <div className="grid grid-cols-3 items-center p-3">
                          <div className="flex items-center space-x-2">
                            {/* Left side - could add controls here */}
                          </div>
                          
                          {/* Centered title */}
                          <div className="flex justify-center items-center">
                            <h2 className="text-white text-lg font-bold whitespace-nowrap truncate max-w-full px-2">
                              {view.destination} viewed from {view.source}
                            </h2>
                          </div>
                          
                          <div className="flex items-center justify-end space-x-2">
                            {/* Right side - could add controls here */}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Small distance notifier for fullscreen when details panel is hidden */}
                    {isFullscreen && detailsPanelState === 'hidden' && (
                      <div className="fixed top-20 left-4 z-[9999999] bg-gray-900/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-cyan-500/30 shadow-[0_0_12px_rgba(0,180,255,0.2)]">
                        <div className="flex items-center gap-2">
                          <Ruler className="h-4 w-4 text-cyan-400" />
                          <span className="text-white font-semibold text-sm">
                            <span className="text-cyan-400">Distance:</span> {distance.value.toLocaleString()} <span className="text-cyan-400">{distance.unit}</span>
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Image controls toolbar */}
                    <div className={`absolute ${isFullscreen ? 'top-20 left-1/2 -translate-x-1/2' : 'bottom-4 left-1/2 -translate-x-1/2'} bg-gray-900/60 backdrop-blur-sm rounded-full border border-cyan-500/20 shadow-[0_0_12px_rgba(0,180,255,0.15)] px-2 py-1 flex items-center space-x-2 z-50`}>
                      {/* Zoom controls - out, scrubber, live scale readout, in */}
                      <button
                        type="button"
                        onClick={() => zoomAtFrameCentre(zoom / zoomStep)}
                        className="p-2 rounded-full text-white hover:bg-cyan-600/40 transition-colors disabled:opacity-50"
                        disabled={zoom <= 1}
                        aria-label="Zoom out"
                      >
                        <ZoomOut className="h-5 w-5" />
                        <span className="sr-only">Zoom out (-)</span>
                      </button>

                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={0.5}
                        value={zoomToSlider(zoom)}
                        onChange={(e) => zoomAtFrameCentre(sliderToZoom(Number(e.target.value)))}
                        className="zoom-scrubber hidden sm:block"
                        style={{ ['--zoom-fill' as string]: `${zoomToSlider(zoom)}%` }}
                        aria-label="Zoom level"
                        aria-valuetext={`${Math.round(zoom * 100)} percent`}
                      />

                      {/* Persistent readout - click to return to 100% and clear the pan */}
                      <button
                        type="button"
                        onClick={resetView}
                        className="px-2 py-1 rounded-full text-white text-xs font-medium tabular-nums hover:bg-cyan-600/40 transition-colors min-w-[3.25rem]"
                        title="Reset to 100%"
                        aria-label={`Zoom ${Math.round(zoom * 100)} percent. Reset to 100 percent`}
                      >
                        {Math.round(zoom * 100)}%
                        <span className="sr-only"> - reset view (0)</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => zoomAtFrameCentre(zoom * zoomStep)}
                        className="p-2 rounded-full text-white hover:bg-cyan-600/40 transition-colors disabled:opacity-50"
                        disabled={zoom >= maxZoom}
                        aria-label="Zoom in"
                      >
                        <ZoomIn className="h-5 w-5" />
                        <span className="sr-only">Zoom in (+)</span>
                      </button>

                      <div className="w-px h-6 bg-cyan-500/20" />

                      {/* Panoramic view toggle for wide images */}
                      {isWideImage && (
                        <>
                          <button
                            type="button"
                            onClick={togglePanoramicView}
                            className={cn(
                              "p-2 rounded-full text-white transition-colors",
                              showWidePanoramicView ? "bg-cyan-600/40" : "hover:bg-cyan-600/20"
                            )}
                            aria-label={showWidePanoramicView ? "Exit panoramic view" : "Enter panoramic view"}
                          >
                            <Move className="h-5 w-5" />
                            <span className="sr-only">{showWidePanoramicView ? "Exit" : "Enter"} Panoramic View (P)</span>
                          </button>
                        </>
                      )}
                      
                      {/* Layout adjustment: keep this divider */}
                      <div className="w-px h-6 bg-cyan-500/20" />
                      
                      <button
                        type="button"
                        onClick={handleFullscreen}
                        className={cn(
                          "p-2 rounded-full text-white transition-colors",
                          isFullscreen ? "bg-cyan-600/40" : "hover:bg-cyan-600/20"
                        )}
                        aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                      >
                        <Maximize className="h-5 w-5" />
                        <span className="sr-only">{isFullscreen ? "Exit" : "Enter"} Fullscreen (F)</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={downloadImage}
                        className="p-2 rounded-full text-white hover:bg-cyan-600/40 transition-colors"
                        aria-label="Download image"
                      >
                        <Download className="h-5 w-5" />
                        <span className="sr-only">Download (D)</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={toggleOverlay}
                        className="p-2 rounded-full text-white hover:bg-cyan-600/40 transition-colors"
                        aria-label="Toggle overlay"
                      >
                        <EyeOff className="h-5 w-5" />
                        <span className="sr-only">Hide UI (O)</span>
                      </button>
                      
                      {/* Auto-cycle controls - only in fullscreen mode */}
                      {isFullscreen && onNext && (
                        <>
                          <div className="w-px h-6 bg-cyan-500/20" />
                          
                          {/* Auto-cycle toggle button */}
                          <button
                            type="button"
                            onClick={isAutoCycling ? stopAutoCycle : startAutoCycle}
                            className={cn(
                              "p-2 rounded-full text-white transition-colors flex items-center gap-1.5",
                              isAutoCycling 
                                ? "bg-green-600/40 hover:bg-green-600/60" 
                                : "hover:bg-cyan-600/40"
                            )}
                            aria-label={isAutoCycling ? "Stop auto-cycle" : "Start auto-cycle"}
                          >
                            {isAutoCycling ? (
                              <Pause className="h-5 w-5" />
                            ) : (
                              <Play className="h-5 w-5" />
                            )}
                            <span className="text-xs font-medium">
                              {isAutoCycling ? 'Stop' : 'Auto'}
                            </span>
                          </button>
                          
                          {/* Interval selector button */}
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setShowIntervalSelector(!showIntervalSelector)}
                              className="p-2 rounded-full text-white hover:bg-cyan-600/40 transition-colors flex items-center gap-1"
                              aria-label="Select auto-cycle interval"
                            >
                              <Clock className="h-4 w-4" />
                              <span className="text-xs font-medium">
                                {INTERVAL_OPTIONS.find(opt => opt.value === autoCycleInterval)?.label}
                              </span>
                            </button>
                            
                            {/* Interval dropdown */}
                            {showIntervalSelector && (
                              <div className="absolute top-12 left-0 w-48 bg-gray-900/90 backdrop-blur-md rounded-lg border border-cyan-500/20 shadow-[0_0_15px_rgba(0,180,255,0.15)] overflow-hidden z-[999999]">
                                <div className="px-3 py-2 bg-gradient-to-r from-cyan-900/40 to-blue-900/30 border-b border-cyan-500/20">
                                  <h3 className="text-sm font-medium text-white">Auto-cycle Interval</h3>
                                </div>
                                <div className="p-2">
                                  {INTERVAL_OPTIONS.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => {
                                        setAutoCycleInterval(option.value);
                                        setShowIntervalSelector(false);
                                        
                                        // Restart auto-cycle with new interval if currently running
                                        if (isAutoCycling) {
                                          stopAutoCycle();
                                          // Start with new interval immediately
                                          setTimeout(() => {
                                            startAutoCycle();
                                          }, 50);
                                        }
                                      }}
                                      className={cn(
                                        "w-full px-3 py-2 text-sm rounded transition-colors text-left",
                                        autoCycleInterval === option.value
                                          ? "bg-cyan-600/40 text-white"
                                          : "text-cyan-300/80 hover:bg-cyan-600/20 hover:text-white"
                                      )}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Enhanced navigation buttons with larger size, padding, and shadow */}
                    {onPrevious && (
                      <button 
                        type="button" 
                        onClick={onPrevious}
                        className="absolute top-1/2 left-6 -translate-y-1/2 p-4 bg-gray-900/60 backdrop-blur-sm hover:bg-cyan-600/40 rounded-full text-white transition-colors z-50 shadow-lg border border-cyan-500/20 shadow-[0_0_12px_rgba(0,180,255,0.15)]"
                        aria-label="Previous view"
                      >
                        <ChevronLeft className="h-8 w-8" />
                      </button>
                    )}
                    
                    {onNext && (
                      <button 
                        type="button" 
                        onClick={onNext}
                        className="absolute top-1/2 right-6 -translate-y-1/2 p-4 bg-gray-900/60 backdrop-blur-sm hover:bg-cyan-600/40 rounded-full text-white transition-colors z-50 shadow-lg border border-cyan-500/20 shadow-[0_0_12px_rgba(0,180,255,0.15)]"
                        aria-label="Next view"
                      >
                        <ChevronRight className="h-8 w-8" />
                      </button>
                    )}

                    {/* Notes button for fullscreen mode - only when there are notes */}
                    {isFullscreen && hasNotes && (
                      <button
                        ref={notesButtonRef}
                        type="button"
                        onClick={toggleNotesPanel}
                        className="absolute top-[60px] right-[120px] p-2 bg-amber-500/25 backdrop-blur-sm rounded-full text-amber-50 hover:bg-amber-500/40 transition-colors border border-amber-400/50 shadow-[0_0_10px_rgba(251,191,36,0.35)] z-[99999999]"
                        aria-label="View notes"
                      >
                        <span className="font-mono text-sm font-bold">Notes</span>
                      </button>
                    )}

                    {/* Help button positioned conditionally */}
                    <button
                      type="button"
                      onClick={() => {
                        // Toggle keyboard shortcuts tooltip
                        const element = document.getElementById(isFullscreen ? 'keyboard-shortcuts-top' : 'keyboard-shortcuts-bottom');
                        if (element) {
                          element.classList.toggle('hidden');
                        }
                      }}
                      className={`${isFullscreen ? 'absolute top-[60px] right-4' : 'p-2 bg-gray-900/60 backdrop-blur-sm rounded-full'}
                        text-white hover:bg-cyan-600/40 transition-colors border border-cyan-500/20 shadow-[0_0_8px_rgba(0,200,255,0.2)]
                        ${isFullscreen ? 'p-2 bg-gray-900/80 backdrop-blur-sm rounded-full z-[99999999]' : ''}`}
                      aria-label="Keyboard shortcuts"
                    >
                      <span className="font-mono text-sm font-bold">Help</span>
                    </button>

                    {/* Keyboard shortcuts tooltip for top help button (fullscreen) */}
                    {isFullscreen && (
                      <div 
                        id="keyboard-shortcuts-top"
                        className="hidden absolute top-[100px] right-4 w-64 bg-gray-900/90 backdrop-blur-md rounded-lg text-white border border-cyan-500/20 shadow-[0_0_15px_rgba(0,180,255,0.15)] overflow-hidden z-[99999999]"
                      >
                        <div className="px-4 py-2 bg-gradient-to-r from-cyan-900/40 to-blue-900/30 border-b border-cyan-500/20">
                          <h3 className="text-sm font-medium text-white">Keyboard Shortcuts</h3>
                        </div>
                        <div className="p-3 space-y-1.5 max-h-80 overflow-y-auto">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Previous/Next</span>
                            <span className="text-xs text-white font-mono">←/→</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Zoom In/Out</span>
                            <span className="text-xs text-white font-mono">+/- or scroll</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Reset View</span>
                            <span className="text-xs text-white font-mono">0</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Fullscreen</span>
                            <span className="text-xs text-white font-mono">F</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Panoramic View</span>
                            <span className="text-xs text-white font-mono">P</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Hide UI</span>
                            <span className="text-xs text-white font-mono">O</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Download</span>
                            <span className="text-xs text-white font-mono">D</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-cyan-300/80">Close</span>
                            <span className="text-xs text-white font-mono">ESC</span>
                          </div>
                          {isFullscreen && onNext && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Auto-cycle (fullscreen)</span>
                              <span className="text-xs text-white font-mono">A</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Help button positioned near bottom-right controls */}
                    {!isFullscreen && (
                      <div className="absolute bottom-6 right-6 z-[9999] flex flex-row gap-2 items-end">
                        {/* Notes button - only when there are notes */}
                        {hasNotes && (
                        <button
                          ref={notesButtonRef}
                          type="button"
                          onClick={toggleNotesPanel}
                          className="p-2 bg-amber-500/25 backdrop-blur-sm rounded-full text-amber-50 hover:bg-amber-500/40 transition-colors border border-amber-400/50 shadow-[0_0_10px_rgba(251,191,36,0.35)]"
                          aria-label="View notes"
                        >
                          <span className="font-mono text-sm font-bold">Notes</span>
                        </button>
                        )}

                        {/* Help button */}
                        <button
                          type="button"
                          onClick={() => {
                            // Toggle keyboard shortcuts tooltip
                            const element = document.getElementById('keyboard-shortcuts-bottom');
                            if (element) {
                              element.classList.toggle('hidden');
                            }
                          }}
                          className="p-2 bg-gray-900/60 backdrop-blur-sm rounded-full text-white hover:bg-cyan-600/40 transition-colors border border-cyan-500/20 shadow-[0_0_8px_rgba(0,200,255,0.2)]"
                          aria-label="Keyboard shortcuts"
                        >
                          <span className="font-mono text-sm font-bold">Help</span>
                        </button>

                        {/* Show Details button - only when panel is hidden */}
                        {isFullscreen && detailsPanelState === 'hidden' && (
                          <button 
                            type="button"
                            onClick={togglePanelState}
                            className="flex items-center gap-2 px-4 py-3 bg-gray-900/80 backdrop-blur-sm 
                              hover:bg-cyan-600/40 rounded-lg text-white font-medium transition-colors 
                              border border-cyan-500/30 shadow-[0_0_12px_rgba(0,180,255,0.2)]
                              hover:border-cyan-400/50 hover:shadow-[0_0_16px_rgba(0,180,255,0.3)]"
                            aria-label="Show details panel"
                          >
                            <ChevronUp className="h-5 w-5 text-cyan-400" />
                            <span className="text-sm">Show Details</span>
                          </button>
                        )}

                        {/* Keyboard shortcuts tooltip for bottom help button */}
                        <div 
                          id="keyboard-shortcuts-bottom"
                          className="hidden absolute bottom-12 right-0 w-64 bg-gray-900/90 backdrop-blur-md rounded-lg text-white border border-cyan-500/20 shadow-[0_0_15px_rgba(0,180,255,0.15)] overflow-hidden"
                        >
                          <div className="px-4 py-2 bg-gradient-to-r from-cyan-900/40 to-blue-900/30 border-b border-cyan-500/20">
                            <h3 className="text-sm font-medium text-white">Keyboard Shortcuts</h3>
                          </div>
                          <div className="p-3 space-y-1.5 max-h-80 overflow-y-auto">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Previous/Next</span>
                              <span className="text-xs text-white font-mono">←/→</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Zoom In/Out</span>
                              <span className="text-xs text-white font-mono">+/- or scroll</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Reset View</span>
                              <span className="text-xs text-white font-mono">0</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Fullscreen</span>
                              <span className="text-xs text-white font-mono">F</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Panoramic View</span>
                              <span className="text-xs text-white font-mono">P</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Hide UI</span>
                              <span className="text-xs text-white font-mono">O</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Download</span>
                              <span className="text-xs text-white font-mono">D</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-cyan-300/80">Close</span>
                              <span className="text-xs text-white font-mono">ESC</span>
                            </div>
                            {isFullscreen && onNext && (
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-cyan-300/80">Auto-cycle (fullscreen)</span>
                                <span className="text-xs text-white font-mono">A</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show Details button for fullscreen mode when panel is hidden */}
                    {isFullscreen && detailsPanelState === 'hidden' && (
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[9999]">
                        <button 
                          type="button"
                          onClick={togglePanelState}
                          className="flex items-center gap-2 px-4 py-3 bg-gray-900/80 backdrop-blur-sm 
                            hover:bg-cyan-600/40 rounded-lg text-white font-medium transition-colors 
                            border border-cyan-500/30 shadow-[0_0_12px_rgba(0,180,255,0.2)]
                            hover:border-cyan-400/50 hover:shadow-[0_0_16px_rgba(0,180,255,0.3)]"
                          aria-label="Show details panel"
                        >
                          <ChevronUp className="h-5 w-5 text-cyan-400" />
                          <span className="text-sm">Show Details</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
                
                {/* Show minimal controls even when overlay is hidden */}
                {!showOverlay && (
                  <button
                    type="button"
                    onClick={toggleOverlay}
                    className="absolute bottom-4 right-4 p-2 bg-gray-900/60 backdrop-blur-sm rounded-full text-white hover:bg-cyan-600/40 transition-colors z-50 border border-cyan-500/20 shadow-[0_0_8px_rgba(0,200,255,0.2)]"
                    aria-label="Show controls"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Caption moved to top bar */}

            {/* Comprehensive data display - now a collapsible panel */}
            {isFullscreen ? (
              <div 
                ref={panelRef}
                className="fixed left-0 right-0 bottom-0 bg-gradient-to-b from-[#0a1222]/90 to-[#071018]/95 
                  transition-all duration-300 ease-in-out shadow-[0_-4px_12px_rgba(0,0,0,0.3)] 
                  border-t border-cyan-500/30 backdrop-blur-md z-[99999] overflow-hidden"
                style={{ 
                  maxHeight: '75vh',
                  height: detailsPanelState === 'expanded' ? `${PANEL_HEIGHTS.expanded}px` : 
                    detailsPanelState === 'collapsed' ? `${PANEL_HEIGHTS.collapsed}px` : 
                    '0px',
                  transform: detailsPanelState === 'hidden' ? 'translateY(100%)' : 'translateY(0)',
                  opacity: detailsPanelState === 'hidden' ? 0 : 1,
                  bottom: 0,
                  position: 'fixed'
                }}
              >
                {/* Simple Panel header without drag functionality */}
                {/* <div className="absolute top-0 left-0 right-0 h-12 z-[99999] bg-gradient-to-r from-cyan-900/40 to-blue-900/30 border-b border-cyan-500/30 backdrop-blur-sm">
                </div> */}

                {/* Panel content with cleaner spacing */}
                <div className="absolute top-12 left-0 right-0 bottom-0 overflow-y-auto p-5 custom-scrollbar" 
                     style={{ 
                       overscrollBehavior: 'contain',
                       WebkitOverflowScrolling: 'touch' 
                     }}>
                  {/* Original content section with cleaner layout */}
                  <div className="flex flex-col space-y-3 max-w-7xl mx-auto overflow-visible">
                    {/* Distance - prominently displayed with Credits */}
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="flex items-center gap-3">
                        <div className="font-mono relative">
                          <div className="absolute -top-2.5 -left-2 text-xs text-cyan-500/50 font-semibold tracking-[0.2em]">DISTANCE</div>
                          <div className="text-2xl font-bold text-white tracking-tight mt-1.5">
                            {distance.value.toLocaleString()} <span className="text-cyan-400 font-semibold">{distance.unit}</span>
                            <span className="text-sm text-cyan-300/70 ml-2 font-normal">
                              ({otherValue} {otherUnit})
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Status indicator moved to middle - with cleaner styling */}
                      <div className="absolute left-1/2 -translate-x-1/2 w-40 flex items-center justify-center">
                        <div className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-full w-full justify-center",
                          "bg-gray-900/70 backdrop-blur-sm shadow border border-gray-700/50"
                        )}>
                          <span className={cn(
                            "w-3 h-3 rounded-full relative",
                            getStatusColor(view.status)
                          )}>
                            <span className={`absolute inset-0 rounded-full ${getStatusColor(view.status)} animate-ping opacity-50`} />
                          </span>
                          <span className="text-base font-medium capitalize text-white">
                            {view.status === 'ifclear' ? 'if clear' : view.status}
                          </span>
                        </div>
                      </div>

                      {/* Credit information to the right */}
                      {(view.resource_3 || view.resource_4) && (
                        <div className="flex items-center gap-3 border-l border-gray-700/50 pl-4 py-2">
                          <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/20 p-2 rounded-full shadow-inner border border-cyan-500/30">
                            <Info className="h-5 w-5 text-cyan-400" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium">Credits</span>
                            <div className="flex items-center gap-2 mt-1">
                              {view.resource_4 && (
                                <span className="text-white text-sm">{view.resource_4}</span>
                              )}
                              {view.resource_3 && (
                                <a 
                                  href={view.resource_3} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-cyan-400 hover:text-cyan-300 underline text-sm"
                                >
                                  View Source
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Record ID display in info panel - very subtle */}
                    <div className="absolute top-2 right-3">
                      <span className="text-[12px] text-gray-500/40 font-mono">ID: {view.id}</span>
                    </div>

                    {/* Source & Destination with connecting arrow - adapted for fullscreen mode */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative my-6 md:grid-rows-[auto_1fr]">
                      {/* Connecting line between source and destination */}
                      <div className="absolute top-12 left-[38%] right-[38%] h-0.5 bg-gradient-to-r from-cyan-400/20 via-cyan-500/40 to-blue-500/20 hidden md:block" />
                      
                      {/* Source details - header */}
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="h-5 w-5 text-cyan-400" />
                        <h3 className="text-lg font-semibold text-white tracking-wide">Source</h3>
                      </div>
                      
                      {/* Destination details - header */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white tracking-wide">Destination</h3>
                        <Navigation className="h-5 w-5 text-blue-400" />
                      </div>
                      
                      {/* Source details - content box */}
                      <div className="bg-gray-900/50 backdrop-blur-sm border-none rounded-lg p-4 space-y-3 shadow-md hover:shadow-lg transition-shadow border-l-2 border-cyan-400/30 relative overflow-hidden group flex flex-col h-full">
                        <p className="text-lg font-medium text-white group-hover:text-cyan-100 transition-colors">{view.source}</p>
                        
                        <div className="pt-2 grid grid-cols-2 gap-4 mt-auto">
                          <div>
                            <p className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium flex items-center mb-1.5">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/50 mr-1.5" />
                              State
                            </p>
                            <p className="text-sm text-white">{view.state}</p>
                          </div>
                          <div>
                            <p className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium flex items-center">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/50 mr-1.5" />
                              Country
                            </p>
                            <p className="text-sm text-white">{view.country}</p>
                          </div>
                            
                          {view.continent && (
                            <div className="col-span-2">
                              <p className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium flex items-center">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/50 mr-1.5" />
                                Continent
                              </p>
                              <p className="text-sm text-white">{view.continent}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Destination details - content box */}
                      <div className="bg-gray-900/50 backdrop-blur-sm border-none rounded-lg p-4 space-y-3 shadow-md hover:shadow-lg transition-shadow border-r-2 border-blue-500/30 relative overflow-hidden group flex flex-col h-full">
                        {/* Share button integrated with destination */}
                        <div className="absolute top-3 right-3">
                          <button
                            type="button"
                            onClick={onShare}
                            className="mr-4 flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 backdrop-blur-sm text-white rounded-full hover:from-cyan-500/40 hover:to-blue-500/40 transition-all shadow-sm border border-cyan-500/20 scale-90"
                          >
                            <Share2 className="h-3 w-3" />
                            <span className="text-xs font-medium">Share</span>
                          </button>
                        </div>
                        
                        <p className="text-lg font-medium text-white group-hover:text-blue-100 transition-colors">{view.destination}</p>
                        
                        <div className="pt-2 grid grid-cols-2 gap-4 mt-auto">
                          <div>
                            <p className="text-xs text-blue-300/70 uppercase tracking-wider font-medium flex items-center mb-1.5">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/50 mr-1.5" />
                              State
                            </p>
                            <p className="text-sm text-white">{view.destinationState}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-300/70 uppercase tracking-wider font-medium flex items-center">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/50 mr-1.5" />
                              Country
                            </p>
                            <p className="text-sm text-white">{view.destinationCountry || view.country}</p>
                          </div>
                          
                          {view.destinationContinent && (
                            <div className="col-span-2">
                              <p className="text-xs text-blue-300/70 uppercase tracking-wider font-medium flex items-center">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/50 mr-1.5" />
                                Continent
                              </p>
                              <p className="text-sm text-white">{view.destinationContinent}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Record ID display in info panel - very subtle */}
                    <div className="absolute top-2 right-3">
                      <span className="text-[12px] text-gray-500/40 font-mono">ID: {view.id}</span>
                    </div>

                    {/* Optional notes display - if there are notes */}
                    {view.notes && (
                      <div className="mt-2 bg-gray-900/30 border border-cyan-500/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <StickyNote className="h-4 w-4 text-cyan-400" />
                          <h3 className="text-sm font-medium text-white">Notes</h3>
                        </div>
                        <p className="text-sm text-white/90 whitespace-pre-wrap">{view.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Always visible toggle button - enhanced for better visibility */}
                {isFullscreen && (
                  <button 
                    type="button"
                    className={`fixed left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out
                      ${detailsPanelState === 'hidden' 
                        ? 'hidden' 
                        : 'bottom-0 w-16 h-7 bg-gradient-to-r from-cyan-800 to-blue-800 rounded-t-xl hover:from-cyan-700 hover:to-blue-700 border border-cyan-500/30 z-[999999]'
                      }
                      cursor-pointer flex items-center justify-center group shadow-lg font-medium`}
                    onClick={togglePanelState}
                    style={{
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      zIndex: detailsPanelState === 'hidden' ? 99999999 : 999999
                    }}
                    aria-label={detailsPanelState === 'hidden' 
                      ? "Show details panel" 
                      : (detailsPanelState === 'expanded' ? "Collapse details panel" : "Hide details panel")
                    }
                  >
                    {detailsPanelState === 'expanded' ? (
                      <ChevronDown className="h-5 w-5 text-cyan-300 group-hover:text-white" />
                    ) : (
                      <div className="flex items-center gap-1">
                        <ChevronDown className="h-5 w-5 text-cyan-300 group-hover:text-white" />
                      </div>
                    )}
                  </button>
                )}
              </div>
            ) : (
              // Use original details panel for non-fullscreen mode
              <div className={`w-full bg-gradient-to-b from-[#0a1222] to-[#071018] p-5 pb-4 shadow-inner relative min-h-[360px] max-h-[67vh] overflow-auto transition-all duration-300 ${isRegularPanelVisible ? '' : 'h-0 min-h-0 p-0 overflow-hidden'}`}
                   style={{ 
                     overscrollBehavior: 'contain',
                     WebkitOverflowScrolling: 'touch'
                   }}>
                {/* Hide/Show panel toggle button with improved styling */}
                <button 
                  type="button"
                  onClick={() => setIsRegularPanelVisible(!isRegularPanelVisible)}
                  className="absolute -top-7 left-1/2 -translate-x-1/2 -translate-y-1/2 
                    w-14 h-6 bg-gradient-to-r from-cyan-800 to-blue-800 rounded-t-xl 
                    cursor-pointer flex items-center justify-center group hover:from-cyan-700 hover:to-blue-700 z-10
                    shadow-lg border border-cyan-500/30"
                  aria-label={isRegularPanelVisible ? "Hide details panel" : "Show details panel"}
                >
                  {isRegularPanelVisible ? (
                    <ChevronDown className="h-4 w-4 text-cyan-300 group-hover:text-white" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-cyan-300 group-hover:text-white" />
                  )}
                </button>
                
                {/* Original content wrapper with improved scrolling */}
                <div className="flex flex-col space-y-3 max-w-7xl mx-auto overflow-visible">
                  {/* Distance - prominently displayed with Credits */}
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-3">
                      <div className="font-mono relative">
                        <div className="absolute -top-2.5 -left-2 text-xs text-cyan-500/50 font-semibold tracking-[0.2em]">DISTANCE</div>
                        <div className="text-2xl font-bold text-white tracking-tight mt-1.5">
                          {distance.value.toLocaleString()} <span className="text-cyan-400 font-semibold">{distance.unit}</span>
                          <span className="text-sm text-cyan-300/70 ml-2 font-normal">
                            ({otherValue} {otherUnit})
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Status indicator moved to middle - with cleaner styling */}
                    <div className="absolute left-1/2 -translate-x-1/2 w-40 flex items-center justify-center">
                      <div className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-full w-full justify-center",
                        "bg-gray-900/70 backdrop-blur-sm shadow border border-gray-700/50"
                      )}>
                        <span className={cn(
                          "w-3 h-3 rounded-full relative",
                          getStatusColor(view.status)
                        )}>
                          <span className={`absolute inset-0 rounded-full ${getStatusColor(view.status)} animate-ping opacity-50`} />
                        </span>
                        <span className="text-base font-medium capitalize text-white">
                          {view.status === 'ifclear' ? 'if clear' : view.status}
                        </span>
                      </div>
                    </div>

                    {/* Credit information to the right */}
                    {(view.resource_3 || view.resource_4) && (
                      <div className="flex items-center gap-3 border-l border-gray-700/50 pl-4 py-2">
                        <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/20 p-2 rounded-full shadow-inner border border-cyan-500/30">
                          <Info className="h-5 w-5 text-cyan-400" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium">Credits</span>
                          <div className="flex items-center gap-2 mt-1">
                            {view.resource_4 && (
                              <span className="text-white text-sm">{view.resource_4}</span>
                            )}
                            {view.resource_3 && (
                              <a 
                                href={view.resource_3} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-cyan-400 hover:text-cyan-300 underline text-sm"
                              >
                                View Source
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Record ID display in info panel - very subtle */}
                  <div className="absolute top-2 right-3">
                    <span className="text-[12px] text-gray-500/40 font-mono">ID: {view.id}</span>
                  </div>

                  {/* Source & Destination with connecting arrow - improved spacing for regular view */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative my-6">
                    {/* Connecting line between source and destination */}
                    <div className="absolute top-12 left-[38%] right-[38%] h-0.5 bg-gradient-to-r from-cyan-400/20 via-cyan-500/40 to-blue-500/20 hidden md:block" />
                    
                    {/* Source details - enhanced spacing */}
                    <div className="space-y-3 relative">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="h-5 w-5 text-cyan-400" />
                        <h3 className="text-lg font-semibold text-white tracking-wide">Source</h3>
                        <div className="h-0.5 flex-grow bg-blue-500/20 mr-2" />
                      </div>
                      
                      <div className="min-h-[220px] bg-gray-900/50 backdrop-blur-sm border-none rounded-lg p-4 space-y-3 shadow-md hover:shadow-lg transition-shadow border-l-2 border-cyan-400/30 relative overflow-hidden group">
                        <p className="text-lg font-medium text-white group-hover:text-cyan-100 transition-colors">{view.source}</p>
                        
                        <div className="pt-2 grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium flex items-center mb-1.5">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/50 mr-1.5" />
                              State
                            </p>
                            <p className="text-sm text-white">{view.state}</p>
                          </div>
                          <div>
                            <p className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium flex items-center">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/50 mr-1.5" />
                              Country
                            </p>
                            <p className="text-sm text-white">{view.country}</p>
                          </div>
                          
                          {view.continent && (
                            <div className="col-span-2">
                              <p className="text-xs text-cyan-300/70 uppercase tracking-wider font-medium flex items-center">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/50 mr-1.5" />
                                Continent
                              </p>
                              <p className="text-sm text-white">{view.continent}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Destination details - enhanced spacing */}
                    <div className="space-y-3 relative">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-0.5 flex-grow bg-blue-500/20 mr-2" />
                        <h3 className="text-lg font-semibold text-white tracking-wide">Destination</h3>
                        <Navigation className="h-5 w-5 text-blue-400" />
                      </div>
                      
                      <div className="min-h-[220px] bg-gray-900/50 backdrop-blur-sm border-none rounded-lg p-4 space-y-3 shadow-md hover:shadow-lg transition-shadow border-r-2 border-blue-500/30 relative overflow-hidden group">
                        {/* Share button integrated with destination */}
                        <div className="absolute top-3 right-3">
                          <button
                            type="button"
                            onClick={onShare}
                            className="mr-4 flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 backdrop-blur-sm text-white rounded-full hover:from-cyan-500/40 hover:to-blue-500/40 transition-all shadow-sm border border-cyan-500/20 scale-90"
                          >
                            <Share2 className="h-3 w-3" />
                            <span className="text-xs font-medium">Share</span>
                          </button>
                        </div>
                        
                        <p className="text-lg font-medium text-white group-hover:text-blue-100 transition-colors">{view.destination}</p>
                        
                        <div className="pt-2 grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-blue-300/70 uppercase tracking-wider font-medium flex items-center mb-1.5">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/50 mr-1.5" />
                              State
                            </p>
                            <p className="text-sm text-white">{view.destinationState}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-300/70 uppercase tracking-wider font-medium flex items-center">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/50 mr-1.5" />
                              Country
                            </p>
                            <p className="text-sm text-white">{view.destinationCountry || view.country}</p>
                          </div>
                          
                          {view.destinationContinent && (
                            <div className="col-span-2">
                              <p className="text-xs text-blue-300/70 uppercase tracking-wider font-medium flex items-center">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400/50 mr-1.5" />
                                Continent
                              </p>
                              <p className="text-sm text-white">{view.destinationContinent}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes Popup - guarded too, so stepping to a view without notes
            while the panel is open cannot leave an empty panel on screen */}
        {hasNotes && (() => {
          return (
            <NotesPopup
              isOpen={showNotesPanel}
              onClose={() => setShowNotesPanel(false)}
              notes={view.notes}
              viewTitle={`${view.source} to ${view.destination}`}
              triggerRef={notesButtonRef}
            />
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}